import { v4 } from 'uuid';
import { createSelector } from 'reselect';
import filter from 'lodash/filter';
import get from 'lodash/get';
import map from 'lodash/map';
import { isCreateAutomationEnabled } from 'selectors/featureToggles';
import { getCallableIntegrations } from 'utils/flow/callableTrigger';
import { scriptStepTypes } from '@xmatters/common/constants/scriptSteps';
import { triggerTypeIconPaths } from 'xm-constants/triggerTypes';
import { isEverbridgeFlowDesignerProduct } from 'utils/products';
import { IntegrationOperation } from 'xm-constants/integration';

const getTriggerLookupBase = () => ({
  uuid: v4(),
  integrationType: 'OUTBOUND_WEBHOOK',
  scriptStepType: 'OUTBOUND_WEBHOOK',
  isBuiltIn: true,
});

// see com.invoqsystems.apex.services.reportstatus.CallbackType
export const triggerObjectLookUp = {
  STATUS: {
    ...getTriggerLookupBase(),
    name: 'Alert Status Updates',
    id: 'STATUS',
    icon: triggerTypeIconPaths.STATUS,
    descriptionId: 'DESCRIPTION_STATUS',
  },
  DELIVERYSTATUS: {
    ...getTriggerLookupBase(),
    name: 'Device Delivery Updates',
    id: 'DELIVERYSTATUS',
    icon: triggerTypeIconPaths.DELIVERYSTATUS,
    descriptionId: 'DESCRIPTION_DELIVERYSTATUS',
  },
  RESPONSE: {
    ...getTriggerLookupBase(),
    name: 'Responses',
    id: 'RESPONSES',
    icon: triggerTypeIconPaths.RESPONSE,
    descriptionId: 'DESCRIPTION_RESPONSE',
  },
  ANNOTATION: {
    ...getTriggerLookupBase(),
    name: 'Alert Comments',
    id: 'ANNOTATION',
    icon: triggerTypeIconPaths.ANNOTATION,
    descriptionId: 'DESCRIPTION_ANNOTATION',
  },
  ESCALATION: {
    ...getTriggerLookupBase(),
    name: 'Escalations',
    id: 'ESCALATION',
    icon: triggerTypeIconPaths.ESCALATION,
    descriptionId: 'DESCRIPTION_ESCALATION',
  },
  TARGETEDRECIPIENTFAILURE: {
    ...getTriggerLookupBase(),
    name: 'Targeted Recipient Failures',
    id: 'TARGETEDRECIPIENTFAILURE',
    icon: triggerTypeIconPaths.TARGETEDRECIPIENTFAILURE,
    descriptionId: 'DESCRIPTION_TARGETEDRECIPIENTFAILURE',
  },
  INBOUND_EMAIL: {
    ...getTriggerLookupBase(),
    name: 'Email',
    id: 'INBOUND_EMAIL',
    icon: triggerTypeIconPaths.EMAIL,
    descriptionId: 'DESCRIPTION_INBOUND_EMAIL',
    integrationType: 'INBOUND_EMAIL',
    scriptStepType: scriptStepTypes.INBOUND_EMAIL,
    paletteCategory: 'TRIGGER_UTILITY',
  },
  FORM_TRIGGER: {
    ...getTriggerLookupBase(),
    name: 'Incident Initiation',
    id: scriptStepTypes.FORM_TRIGGER,
    icon: triggerTypeIconPaths.FORM,
    descriptionId: 'DESCRIPTION_XMATTERS_FORM_TRIGGER',
    integrationType: scriptStepTypes.FORM_TRIGGER, // Maps to VisualizationCanvas view/nodes when dropped on canvas
    scriptStepType: scriptStepTypes.FORM_TRIGGER,
    featureToggle: 'INCIDENT_MANAGEMENT_MVP',
    helpId: 'InitiationTrigger',
  },
  AUTOMATION_TRIGGER: {
    ...getTriggerLookupBase(),
    name: 'Incident Automation',
    id: scriptStepTypes.AUTOMATION_TRIGGER,
    icon: triggerTypeIconPaths.AUTOMATION,
    descriptionId: 'DESCRIPTION_XMATTERS_AUTOMATION_TRIGGER',
    integrationType: scriptStepTypes.AUTOMATION_TRIGGER, // Maps to VisualizationCanvas view/nodes when dropped on canvas
    scriptStepType: scriptStepTypes.FORM_TRIGGER, // sidebar category
    // Must have all FF enabled for this step to show.
    featureToggle: ['INCIDENT_AUTOMATION_ACTIONS', 'INCIDENT_MANAGEMENT_MVP'],
    helpId: 'AutomationTrigger',
  },
  CALLABLE_FLOW_RECEIVER: {
    ...getTriggerLookupBase(),
    name: 'Callable Trigger',
    id: scriptStepTypes.CALLABLE_FLOW_RECEIVER,
    icon: triggerTypeIconPaths.WORKFLOW,
    descriptionId: 'DESCRIPTION_XMATTERS_CALLABLE_TRIGGER',
    integrationType: scriptStepTypes.CUSTOM_HTTP,
    scriptStepType: scriptStepTypes.INBOUND_WEBHOOK,
    paletteCategory: 'TRIGGER_UTILITY',
    featureToggle: 'CALLABLE_FLOW_MVP',
  },
  EBFD_INCIDENT_RESPONSE_TRIGGER: {
    ...getTriggerLookupBase(),
    name: 'Everbridge Incident Responses',
    id: scriptStepTypes.EBFD_INCIDENT_RESPONSE_TRIGGER,
    icon: triggerTypeIconPaths.EBFD_INCIDENT_RESPONSE_TRIGGER,
    descriptionId: 'DESCRIPTION_EBFD_INCIDENT_RESPONSE_TRIGGER',
    integrationType: scriptStepTypes.EBFD_INCIDENT_RESPONSE_TRIGGER,
    scriptStepType: scriptStepTypes.EBFD_INCIDENT_RESPONSE_TRIGGER,
    paletteCategory: 'BUILT_IN_HTTP',
    isEnabledForProduct(productName) {
      return isEverbridgeFlowDesignerProduct(productName);
    },
  },
};

export const triggerWithLegacyObjectLookUp = {
  ...triggerObjectLookUp,
  INBOUND_WEBHOOK: {
    ...getTriggerLookupBase(),
    name: 'Inbound Integration',
    id: 'INBOUND_WEBHOOK',
    integrationType: 'INBOUND_WEBHOOK',
    icon: triggerTypeIconPaths.LEGACY_WEBHOOK,
  },
};

const ALLOW_MULTIPLE = new Set([
  'INBOUND_EMAIL',
  'FORM_TRIGGER',
  'AUTOMATION_TRIGGER',
  'CALLABLE_FLOW_RECEIVER',
  'EBFD_INCIDENT_RESPONSE_TRIGGER',
]);
const ALLOW_NO_FORM = new Set([
  'INBOUND_EMAIL',
  'FORM_TRIGGER',
  'AUTOMATION_TRIGGER',
  'CALLABLE_FLOW_RECEIVER',
  'EBFD_INCIDENT_RESPONSE_TRIGGER',
]);

export const allTriggers = Object.keys(triggerObjectLookUp);
export const allTriggersWithLegacy = Object.keys(triggerWithLegacyObjectLookUp);

/**
 * from integrations to compute available trigger types objects
 */
const getIntegrations = state => state.flowDesigner.uiIntegrations || [];
const getCurrentCanvas = state => state.flowDesigner.planCanvas;

export const getCallableTriggerIntegrations = createSelector(
  [getIntegrations],
  integrations => getCallableIntegrations(integrations),
);

export const getAvailableTriggers = createSelector(
  [getCurrentCanvas, getIntegrations, isCreateAutomationEnabled],
  (canvas, integrations, canCreateAutomation) => {
    const inUse = new Set();
    const form = canvas?.form;
    const formId = form?.id;
    const formIntegrations = formId
      ? integrations.filter(i => i.form?.id === formId)
      : [];

    if (formId) {
      formIntegrations.forEach(i => {
        if (i.triggeredBy) {
          inUse.add(i.triggeredBy);
        }
      });
    }

    // is response-trigger already added to canvas?
    // note that we allow users to add response-trigger despite
    // no response-option is configured.
    if (
      canvas?.blocks?.find(
        b => !b.groupingBlockId && b.step?.type === 'RESPONSES',
      )
    ) {
      inUse.add('RESPONSE');
    }

    const disabled = type => {
      return type !== scriptStepTypes.AUTOMATION_TRIGGER
        ? false
        : !canCreateAutomation;
    };

    if (formId) {
      // Prevent adding the integration if it shares a name with an existing integration
      // (unless it's ALLOW_MULTIPLE, in which case we will append a number).
      const namesInUse = new Set(map(formIntegrations, 'name'));

      // TODO: this should ideally not be copied from VisualizationVanvas.jsx method addNodeIntegration,
      // Instead it would be best if the names were constructed here or similar, and _passed along_.
      const name = (form ? form.name : undefined) || canvas.name;
      const nameBuilder = triggerObj => `${name} - ${triggerObj.name}`;

      const isPresent = (triggerObj, triggerKey) =>
        inUse.has(triggerKey) || namesInUse.has(nameBuilder(triggerObj));
      const isVisible = (triggerObj, triggerKey) =>
        ALLOW_MULTIPLE.has(triggerKey) || !isPresent(triggerObj, triggerKey);

      return filter(triggerObjectLookUp, isVisible).map(entry => {
        return {
          ...entry,
          disabled: disabled(entry.id),
        };
      });
    }
    return allTriggers
      .filter(t => ALLOW_NO_FORM.has(t))
      .map(trigger => {
        const entry = triggerObjectLookUp[trigger];
        return {
          ...entry,
          disabled: disabled(trigger),
        };
      });
  },
);

export const lookupTriggerInfo = integration => {
  if (!integration) {
    return null;
  }
  const { type, triggeredBy, integrationType, operation } = integration;

  if (operation === IntegrationOperation.CALLABLE_TRIGGER) {
    return triggerWithLegacyObjectLookUp.CALLABLE_FLOW_RECEIVER;
  }

  return (
    triggerWithLegacyObjectLookUp[triggeredBy || type] ||
    triggerWithLegacyObjectLookUp[integrationType]
  );
};

export const lookupTriggerName = integration =>
  get(lookupTriggerInfo(integration), 'name');

export function buildIntegrationInfoMap(integrations) {
  return integrations.reduce((acc, i) => {
    const triggerDef = lookupTriggerInfo(i);

    if (triggerDef) {
      const { descriptionId, integrationType } = triggerDef;
      const icon = i.responseOption
        ? triggerTypeIconPaths.SINGLERESPONSE
        : triggerDef.icon;

      acc[i.id] = {
        icon,
        descriptionId,
        integrationType,
        type: i.type,
        migratedOutboundTrigger: i.migratedOutboundTrigger,
      };
    } else {
      acc[i.id] = {};
    }

    return acc;
  }, {});
}
